// Local procedural music generator — 100% client-side, offline, free.
// Synthesizes a multi-instrument arrangement into an AudioBuffer via direct sample math.

const midiToFreq = (m) => 440 * Math.pow(2, (m - 69) / 12);

const SCALES = {
  major: [0, 2, 4, 5, 7, 9, 11],
  minor: [0, 2, 3, 5, 7, 8, 10],
  dorian: [0, 2, 3, 5, 7, 9, 10],
  pentMinor: [0, 3, 5, 7, 10],
};

// Style presets: bpm, root midi, scale, palette flags
export const STYLE_PRESETS = [
  { id: "lofi", label: "Lo-Fi", bpm: 82, root: 57, scale: "dorian", swing: 0.14, warm: true },
  { id: "hiphop", label: "Hip-Hop", bpm: 90, root: 45, scale: "minor", swing: 0.08 },
  { id: "house", label: "House", bpm: 124, root: 48, scale: "minor", four: true },
  { id: "techno", label: "Techno", bpm: 130, root: 43, scale: "minor", four: true, dark: true },
  { id: "trap", label: "Trap", bpm: 140, root: 40, scale: "minor", trap: true },
  { id: "pop", label: "Pop", bpm: 112, root: 52, scale: "major", four: true },
  { id: "ambient", label: "Ambient", bpm: 70, root: 50, scale: "major", pad: true },
  { id: "cinematic", label: "Cinematic", bpm: 88, root: 45, scale: "minor", pad: true, epic: true },
];

export function presetById(id) {
  return STYLE_PRESETS.find((s) => s.id === id) || STYLE_PRESETS[0];
}

// Simple seeded PRNG
function rng(seed) {
  let s = seed >>> 0 || 12345;
  return () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; };
}

function triad(scaleArr, root, degree) {
  const n = scaleArr.length;
  const note = (d) => root + scaleArr[((degree + d) % n + n) % n] + 12 * Math.floor((degree + d) / n);
  return [note(0), note(2), note(4)];
}

// ADSR + waveform written directly into a channel
function addNote(ch, sr, start, dur, freq, gain, type, detune = 0) {
  const n0 = Math.floor(start * sr);
  const nN = Math.min(ch.length, Math.floor((start + dur) * sr));
  const a = 0.006, r = Math.min(dur * 0.5, 0.12);
  const w = 2 * Math.PI * freq / sr;
  const wd = 2 * Math.PI * (freq * Math.pow(2, detune / 1200)) / sr;
  for (let i = n0; i < nN; i++) {
    const t = (i - n0) / sr, rem = (nN - i) / sr;
    let env = 1;
    if (t < a) env = t / a;
    else if (rem < r) env = rem / r;
    const ph = (i - n0) * w, ph2 = (i - n0) * wd;
    let s;
    if (type === "sine") s = Math.sin(ph);
    else if (type === "tri") s = 2 / Math.PI * Math.asin(Math.sin(ph));
    else if (type === "square") s = Math.sin(ph) > 0 ? 0.6 : -0.6;
    else { // saw (two detuned for width)
      const saw = (p) => 2 * ((p / (2 * Math.PI)) % 1) - 1;
      s = 0.5 * saw(ph) + 0.5 * saw(ph2);
    }
    ch[i] += s * gain * env;
  }
}

function addKick(ch, sr, start) {
  const dur = 0.32, n0 = Math.floor(start * sr), nN = Math.min(ch.length, Math.floor((start + dur) * sr));
  for (let i = n0; i < nN; i++) {
    const t = (i - n0) / sr;
    const f = 120 * Math.exp(-t * 22) + 45;
    const env = Math.exp(-t * 9);
    ch[i] += Math.sin(2 * Math.PI * f * t) * env * 0.9;
  }
}

function addNoise(ch, sr, start, dur, gain, hp, rand) {
  const n0 = Math.floor(start * sr), nN = Math.min(ch.length, Math.floor((start + dur) * sr));
  let prev = 0;
  for (let i = n0; i < nN; i++) {
    const t = (i - n0) / sr, rem = (nN - i) / sr;
    const env = Math.min(1, rem / (dur * 0.6));
    let s = rand() * 2 - 1;
    if (hp) { const cur = s - prev; prev = s; s = cur; } // crude high-pass for hats
    ch[i] += s * gain * env;
  }
}

/**
 * Generate a procedural track into an AudioBuffer.
 * @param {AudioContext} ctx
 * @param {{styleId:string, durationSec:number, instrumental:boolean, seed?:number}} opts
 */
export async function generateProceduralMusic(ctx, { styleId, durationSec, instrumental, seed }) {
  const p = presetById(styleId);
  const sr = 44100;
  const dur = Math.max(5, Math.min(240, durationSec || 30));
  const rand = rng(seed || Math.floor(Math.random() * 1e6));
  const beat = 60 / p.bpm;
  const bar = beat * 4;
  const total = Math.ceil(dur * sr);
  const L = new Float32Array(total), R = new Float32Array(total);
  const scale = SCALES[p.scale] || SCALES.minor;
  const prog = [0, 4, 5, 3]; // I - v - vi - IV degrees

  const barCount = Math.ceil(dur / bar);
  for (let b = 0; b < barCount; b++) {
    const t0 = b * bar;
    const degree = prog[b % prog.length];
    const chord = triad(scale, p.root, degree);
    // Pad / chords (both channels, wide)
    if (!(p.trap && b % 2 === 1)) {
      chord.forEach((m, ci) => {
        const g = p.pad ? 0.16 : 0.1;
        addNote(L, sr, t0, bar * 0.98, midiToFreq(m + 12), g, p.dark ? "saw" : "tri", -6);
        addNote(R, sr, t0, bar * 0.98, midiToFreq(m + 12), g, p.dark ? "saw" : "tri", +6);
      });
    }
    // Bass — root, rhythmic
    const bassNote = midiToFreq(p.root - 12 + scale[degree % scale.length]);
    for (let s = 0; s < 4; s++) {
      if (p.trap && rand() < 0.3) continue;
      const bt = t0 + s * beat + (p.swing && s % 2 ? beat * p.swing * 0.3 : 0);
      addNote(L, sr, bt, beat * 0.9, bassNote, 0.5, "square");
      addNote(R, sr, bt, beat * 0.9, bassNote, 0.5, "square");
    }
    // Drums
    for (let s = 0; s < 4; s++) {
      const bt = t0 + s * beat;
      if (p.four || s === 0 || s === 2 || (!p.four && rand() < 0.6)) { addKick(L, sr, bt); addKick(R, sr, bt); }
      if (s === 1 || s === 3) { addNoise(L, sr, bt, 0.16, 0.35, false, rand); addNoise(R, sr, bt, 0.16, 0.35, false, rand); }
      // hats
      const hatDiv = p.trap ? 8 : 4;
      for (let h = 0; h < hatDiv; h++) {
        const ht = bt + (h / hatDiv) * beat + (p.swing && h % 2 ? beat * p.swing * 0.1 : 0);
        if (p.trap && rand() < 0.25) continue;
        addNoise(L, sr, ht, 0.04, 0.12, true, rand); addNoise(R, sr, ht, 0.04, 0.12, true, rand);
      }
    }
    // Lead melody (skip some bars for space; brighter when not instrumental)
    if (!p.pad && (instrumental ? b % 2 === 0 : true)) {
      const notes = 4;
      for (let s = 0; s < notes; s++) {
        if (rand() < 0.25) continue;
        const deg = degree + Math.floor(rand() * 5);
        const m = p.root + 12 + scale[((deg % scale.length) + scale.length) % scale.length];
        const lt = t0 + s * beat + (p.swing ? beat * p.swing : 0);
        const g = instrumental ? 0.14 : 0.2;
        addNote(L, sr, lt, beat * 0.8, midiToFreq(m), g, instrumental ? "tri" : "saw", -3);
        addNote(R, sr, lt, beat * 0.8, midiToFreq(m), g, instrumental ? "tri" : "saw", +3);
      }
    }
  }

  // Simple master: soft-clip + fade in/out + normalize
  const fade = Math.floor(0.05 * sr);
  let peak = 0;
  for (let i = 0; i < total; i++) { peak = Math.max(peak, Math.abs(L[i]), Math.abs(R[i])); }
  const norm = peak > 0 ? 0.89 / peak : 1;
  const softclip = (x) => Math.tanh(x * 1.1);
  for (let i = 0; i < total; i++) {
    let l = softclip(L[i] * norm), r = softclip(R[i] * norm);
    if (i < fade) { l *= i / fade; r *= i / fade; }
    if (i > total - fade) { const f = (total - i) / fade; l *= f; r *= f; }
    L[i] = l; R[i] = r;
  }

  const buffer = ctx.createBuffer(2, total, sr);
  buffer.copyToChannel(L, 0);
  buffer.copyToChannel(R, 1);
  return buffer;
}
